import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./Components/Common/ScrollToTop/ScrollToTop";
import { ToastProvider } from "./Components/Common/Toast/ToastContext";
import { useFCM } from "./hooks/useFCM";
import PushUnsupportedModal from "./Components/Common/PushUnsupportedModal/PushUnsupportedModal";
import "./App.scss";

import AdminDashboard from "./Pages/Admin/AdminDashboard/AdminDashboard";
import ManageUser from "./Pages/Admin/ManageUser/ManageUser";
import AddNewProject from "./Pages/Admin/AddNewProject/AddNewProject";
import ProjectDetail from "./Pages/Admin/ProjectDetail/ProjectDetail";
import PreconAuthoring from "./Pages/Admin/PreconAuthoring/PreconAuthoring";
import GcRequirements from "./Pages/GcPrecon/GcRequirements/GcRequirements";
import GcProjectDetail from "./Pages/GcPrecon/GcProjectDetails/GcProjectDetail";
import ConstructionReportAuthoring from "./Pages/Admin/ConstructionReportAuthoring/ConstructionReportAuthoring";
import ChangeOrder from "./Pages/GcPrecon/ChangeOrder/ChangeOrder";
import LienWaiver from "./Pages/SubContractor/LienWaiver";
import PreconReview from "./Pages/Public/PreconReview";
import DrawReview from "./Pages/Public/DrawReview";
import PreconReportView from "./Pages/Public/PreconReportView";
import SharedDocumentView from "./Pages/Public/SharedDocumentView";
import ProgressReportView from "./Pages/Public/ProgressReportView";
import BankDashboard from "./Pages/Bank/BankDashboard/BankDashboard";
import BankProjectDetail from "./Pages/Bank/BankDashboard/BankProjectDetail";
import OwnerDashboard from "./Pages/Owner/OwnerDashboard";
import Inspector from "./Pages/Inspector/InspectorFi/Inspector";
import InspectorLayout from "./Pages/Inspector/InspectorLayout";
import OwnerProjectDetail from "./Pages/Owner/OwnerProjectDetail";
import OwnerLayout from "./Pages/Owner/OwnerLayout";
import ProtectedRoute, {
  GuestRoute,
} from "./Features/AuthRoutes/ProtectedRoutes";
import InspectorAssign from "./Pages/Admin/InspectorAssign/InspectorAssign";
import AdminLayout from "./Pages/Admin/AdminLayout";
import Notification from "./Pages/Notifications/Notification";
import ReportsPage from "./Pages/Reports/ReportsPage";
import Login from "./Pages/Login/Login";
import DrawRequest from "Pages/GcPrecon/DrawRequest/DrawRequest";
import SubContractor from "Pages/GcPrecon/ManageSubContractor/ManageSubContractor";
import GcDashboard from "Pages/GcPrecon/GcDashboard/GcDashboard";
import GcPreconContainer from "Pages/GcPrecon/GcPreconCommercial/GcPreconContainer";
import GcLayout from "Pages/GcPrecon/GcLayout";
import { ROLES } from "utils/Common/Constant";
import SOVSetup from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup";
import BankLayout from "Pages/Bank/BankLayout";
import CreatePassword from "Pages/Login/CreatePassword";
import ForgotPassword from "Pages/Login/ForgotPassword";
import ResetPassword from "Pages/Login/ResetPassword";
import TwoFactorAuth from "Pages/Login/TwoFactorAuth";
import ManageBank from "Pages/Admin/ManageBank/ManageBank";
import ProfilePage from "Pages/Profile/ProfilePage";
import ProfileLayout from "Pages/Profile/ProfileLayout";

interface AuthUser {
  role: string;
}

const ROLE_REDIRECT: Record<string, string> = {
  admin: "/admin",
  gc: "/gc-dashboard",
  sub: "/lien-waiver",
  lender: "/bank-dashboard",
  owner: "/owner-dashboard",
  inspector: "/project-inspection",
};

function parseAuthUser(raw: string | null): AuthUser | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function FCMInit() {
  useFCM();
  return <PushUnsupportedModal />;
}

export default function App() {
  useEffect(() => {
    const syncAuth = (event: StorageEvent) => {
      const { pathname } = window.location;
      if (
        pathname.startsWith("/create-password") ||
        pathname.startsWith("/forgot-password") ||
        pathname.startsWith("/reset-password")
      )
        return;
      if (event.key === "authChanged") {
        const user = parseAuthUser(localStorage.getItem("user"));
        if (user?.role) {
          window.location.replace(ROLE_REDIRECT[user.role] ?? "/");
        } else {
          window.location.replace("/");
        }
        return;
      }
      if (
        (event.key === "refreshToken" || event.key === "token") &&
        event.newValue === null &&
        !localStorage.getItem("refreshToken")
      ) {
        window.location.replace("/");
      }
    };

    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, []);

  return (
    <ToastProvider>
      <FCMInit />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Public */}
          <Route
            path="/"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route path="/create-password/:token" element={<CreatePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route
            path="/two-factor-auth"
            element={
              <GuestRoute>
                <TwoFactorAuth />
              </GuestRoute>
            }
          />

          {/* ================= ADMIN ================= */}
          <Route
            element={
              <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<AdminDashboard />} />
            <Route
              path="/project-detail/:id/:activeTab?"
              element={<ProjectDetail />}
            />
            <Route path="/manage-users" element={<ManageUser />} />
            <Route path="/manage-bank" element={<ManageBank />} />
            <Route path="/add-new-project" element={<AddNewProject />} />
            <Route
              path="/precon-authoring/:projectId"
              element={<PreconAuthoring />}
            />
            <Route
              path="/construction-report-authoring/:projectId/:drawId"
              element={<ConstructionReportAuthoring />}
            />
            <Route path="/inspector-assignment" element={<InspectorAssign />} />
            <Route
              path="/notifications"
              element={<Notification role="Admin" />}
            />
            <Route path="/reports" element={<ReportsPage />} />
          </Route>

          {/* ================= GENERAL CONTRACTOR ================= */}
          <Route
            element={
              <ProtectedRoute allowedRoles={[ROLES.GC]}>
                <GcLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/gc-dashboard" element={<GcDashboard />} />
            <Route
              path="/gc-precon/:projectId"
              element={<GcPreconContainer />}
            />
            <Route
              path="/gc-requirements/:projectId"
              element={<GcRequirements />}
            />
            <Route
              path="/gc-project-detail/:projectId/:activeTab?"
              element={<GcProjectDetail />}
            />
            <Route path="/draw-request/:projectId" element={<DrawRequest />} />
            <Route
              path="/draw-request/:projectId/:drawId"
              element={<DrawRequest />}
            />
            <Route path="/sub-contractor" element={<SubContractor />} />
            <Route path="/change-order/:projectId" element={<ChangeOrder />} />
            <Route
              path="/gc-sov/:projectId"
              element={<SOVSetup isActive={true} isStandalone={true} />}
            />
            <Route
              path="/gc-notifications"
              element={<Notification role="GC" />}
            />
          </Route>

          {/* ================= INSPECTOR ================= */}
          <Route
            element={
              <ProtectedRoute allowedRoles={[ROLES.INSPECTOR]}>
                <InspectorLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/project-inspection" element={<Inspector />} />
            <Route
              path="/inspector-notifications"
              element={<Notification role="Inspector" />}
            />
          </Route>

          {/* ================= SUB CONTRACTOR ================= */}
          {/* Token-based secure signing link: /g/:token */}
          <Route path="/lien-waiver/:token" element={<LienWaiver />} />
          <Route
            path="/lien-waiver/preview/:projectId/:drawId/:lienWaiverId"
            element={<LienWaiver />}
          />

          {/* ================= PUBLIC (TOKEN-BASED) REPORT LINKS ================= */}
          <Route path="/precon-review/:token" element={<PreconReview />} />
          <Route path="/draw-review/:token" element={<DrawReview />} />
          <Route
            path="/bank-project-detail/:projectId/precon/:resourceId/:token"
            element={<SharedDocumentView />}
          />
          <Route
            path="/bank-project-detail/:projectId/reports/:resourceId/:token"
            element={<SharedDocumentView />}
          />

          {/* ================= BANK / LENDER ================= */}

          {/* Lender project creation — BankLayout provides nav */}
          <Route
            element={
              <ProtectedRoute allowedRoles={[ROLES.LENDER]}>
                <BankLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/bank-dashboard" element={<BankDashboard />} />
            <Route
              path="/bank-project-detail/:projectId/:activeTab?"
              element={<BankProjectDetail />}
            />
            <Route path="/bank-add-project" element={<AddNewProject />} />
            <Route
              path="/lender-notifications"
              element={<Notification role="Lender" />}
            />
          </Route>

          {/* ================= OWNER ================= */}
          <Route
            element={
              <ProtectedRoute allowedRoles={[ROLES.OWNER]}>
                <OwnerLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/owner-dashboard" element={<OwnerDashboard />} />
            <Route
              path="/owner-project-detail/:id/:activeTab?"
              element={<OwnerProjectDetail />}
            />
            <Route
              path="/owner-notifications"
              element={<Notification role="Owner" />}
            />
          </Route>

          {/* ================= PROFILE (all roles) ================= */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  ROLES.ADMIN,
                  ROLES.GC,
                  ROLES.INSPECTOR,
                  ROLES.LENDER,
                  ROLES.OWNER,
                ]}
              >
                <ProfileLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Unauthorized fallback */}
          <Route path="/unauthorized" element={<h1>Access Denied</h1>} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
